import { parseCalendarDate } from '@klothiq/domain';
// ─────────────────────────────────────────────
// KLOTHIQ — My Tasks Page
// ─────────────────────────────────────────────

import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListTodo, Clock, AlertTriangle, Layers, ChevronRight } from 'lucide-react';
import { useMyTasks, type MyTaskItem } from '@/hooks/useTasks';
import TaskDetailModal from './TaskDetailModal';

export default function MyTasksPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedTask, setSelectedTask] = useState<MyTaskItem | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: allTasks = [], isLoading, isError, error, refetch } = useMyTasks();
  const tasks = allTasks.filter((task) => statusFilter === 'all' || task.status === statusFilter);
  const taskId = searchParams.get('taskId');
  useEffect(() => {
    if (taskId) setSelectedTask(allTasks.find((task) => task.id === taskId) ?? null);
    else
      setSelectedTask((current) =>
        current ? (allTasks.find((task) => task.id === current.id) ?? null) : null,
      );
  }, [allTasks, taskId]);

  // Calculate overdue tasks
  const overdueTasks = useMemo(() => {
    return allTasks.filter((t) => t.is_overdue);
  }, [allTasks]);

  // Aggregate metrics
  const counts = useMemo(() => {
    const todo = allTasks.filter((t) => t.status === 'todo').length;
    const inProgress = allTasks.filter((t) => t.status === 'in_progress').length;
    const blocked = allTasks.filter((t) => t.status === 'blocked').length;
    const completed = allTasks.filter((t) => t.status === 'completed').length;
    return {
      total: allTasks.length,
      todo,
      inProgress,
      blocked,
      completed,
      overdue: overdueTasks.length,
    };
  }, [allTasks, overdueTasks]);

  return (
    <div className="flex-1 space-y-6 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto overflow-y-auto">
      {/* Top Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">My Assigned Tasks</h1>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
          Your personal workbench of deliverables across active organizational plans.
        </p>
      </div>

      {/* Overdue Alert Banner if any */}
      {counts.overdue > 0 && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-semibold text-rose-500">
              You have {counts.overdue} overdue task{counts.overdue > 1 ? 's' : ''}!
            </p>
            <p className="text-rose-500/80 mt-0.5">
              Please review your past-due deliverables and update progress or report blockers to
              your team.
            </p>
          </div>
        </div>
      )}

      {/* Status Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {[
          { id: 'all', label: 'All Tasks', count: counts.total },
          { id: 'todo', label: 'To Do', count: counts.todo },
          { id: 'in_progress', label: 'In Progress', count: counts.inProgress },
          { id: 'blocked', label: 'Blocked', count: counts.blocked },
          { id: 'completed', label: 'Completed', count: counts.completed },
        ].map((tab) => {
          const isSelected = statusFilter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`rounded-xl px-3.5 py-1.5 text-xs font-semibold transition-all flex items-center gap-1.5 ${
                isSelected
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-card border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                  isSelected
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Task List */}
      {taskId && !isLoading && !isError && !allTasks.some((task) => task.id === taskId) && (
        <p
          role="status"
          className="rounded-xl border border-border p-4 text-sm text-muted-foreground"
        >
          This task is no longer assigned to you or its plan was archived.
        </p>
      )}
      {isError ? (
        <div role="alert" className="rounded-xl border border-destructive/30 p-5">
          <p>{error instanceof Error ? error.message : 'Your tasks could not be loaded.'}</p>
          <button
            className="mt-3 min-h-11 rounded-xl border border-border px-4"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="py-20 text-center text-sm text-muted-foreground">Loading your tasks...</div>
      ) : tasks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
          <ListTodo className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-foreground">No tasks assigned</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
            {statusFilter !== 'all'
              ? `You don't have any tasks in status "${statusFilter}".`
              : 'You have no assigned tasks in active plans right now. Great job!'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((t) => {
            const taskPriorityStyle =
              {
                urgent: 'text-rose-500 bg-rose-500/10 border-rose-500/20',
                high: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
                normal: 'text-primary bg-primary/10 border-primary/20',
                low: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
              }[t.priority] || 'text-muted-foreground bg-muted border-border';

            const taskStatusStyle =
              {
                completed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
                in_progress: 'bg-primary/10 text-primary border-primary/20',
                blocked: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
                todo: 'bg-muted text-muted-foreground border-border',
                cancelled: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
              }[t.status] || 'bg-muted text-muted-foreground border-border';

            return (
              <div
                key={t.id}
                onClick={() => setSelectedTask(t)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedTask(t);
                  }
                }}
                className="group flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-border bg-card/70 p-5 hover:border-primary/50 hover:bg-card transition-all cursor-pointer shadow-sm"
              >
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${taskPriorityStyle}`}
                    >
                      {t.priority}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${taskStatusStyle}`}
                    >
                      {t.status.replace('_', ' ')}
                    </span>
                    {t.is_overdue && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 border border-rose-500/30 px-2 py-0.5 text-[9px] font-semibold text-rose-500">
                        <Clock className="h-2.5 w-2.5" /> Overdue
                      </span>
                    )}
                  </div>

                  <h3 className="text-sm sm:text-base font-bold text-foreground group-hover:text-primary transition-colors">
                    {t.title}
                  </h3>

                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Layers className="h-3 w-3 text-primary" />
                    <span>Plan:</span>
                    <span className="font-semibold text-foreground truncate">{t.plan_title}</span>
                  </div>

                  {t.blocker_note && (
                    <div className="flex items-center gap-1.5 rounded-lg bg-amber-500/10 p-2 text-xs text-amber-500 font-medium">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      <span className="line-clamp-1">Blocker: {t.blocker_note}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-5 text-xs shrink-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-border/50">
                  {/* Due Date */}
                  <div className="text-left sm:text-right">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                      Due Date
                    </p>
                    <p
                      className={`text-xs font-medium mt-0.5 ${
                        t.is_overdue ? 'text-rose-500 font-semibold' : 'text-foreground'
                      }`}
                    >
                      {t.due_date
                        ? parseCalendarDate(t.due_date)?.toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'No deadline'}
                    </p>
                  </div>

                  {/* Progress */}
                  <div className="text-left sm:text-right min-w-[70px]">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                      Progress
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="h-1.5 w-12 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${t.progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-foreground">{t.progress}%</span>
                    </div>
                  </div>

                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={!!selectedTask}
        onClose={() => {
          setSelectedTask(null);
          const next = new URLSearchParams(searchParams);
          next.delete('taskId');
          setSearchParams(next, { replace: true });
        }}
        task={selectedTask}
        onReload={async () => {
          const result = await refetch();
          if (result.error) throw result.error;
          return result.data?.find((task) => task.id === selectedTask?.id);
        }}
      />
    </div>
  );
}
